export type ReportListItem = {
  name: string;        // 用於組合 reportNames 的前綴
  title: string;       // UI 顯示
  fields: string[];    // 可選欄位 key
  badge?: string;
  note?: string;
  noteWhenAiMaxOnly?: boolean;
};

export function shouldShowReportNote(
  item: Pick<ReportListItem, 'note' | 'noteWhenAiMaxOnly'>,
  extra?: { aiMaxScope?: string },
): boolean {
  if (!item.note) return false;
  if (item.noteWhenAiMaxOnly) return extra?.aiMaxScope === 'ONLY';
  return true;
}

export type GoogleTemplateExtra = {
  adTypes: string[];
  aiMaxScope: 'ALL' | 'ONLY' | 'EXCLUDE';
};

export type MetricsGroup = {
  title: string;       // 分類名稱 (UI 顯示)
  fields: { field: string; label: string }[]; // 欄位 key/label
};

export type AccountCampaign = {
  accountId: string;
  accountName: string;
  campaignId?: string;
  campaignName?: string;
};

export interface AdsPlatformAdapter {
  key: 'google' | 'meta' | 'line';
  displayName: string; // 用於按鈕/標題：ex: "Google Ads", "Meta Ads", "LINE Ads"

  // UI 行為/字串
  nameLabel: string;              // "報表名稱"
  tableNameHeader: string;        // DataGrid 的「xxx 報表名稱」欄
  createButtonText: string;       // "新增xxx報表設定"
  generateQueryEntity: string;    // CreateView 的 queryEntity: googleAds/metaAds/lineAds
  removeUnexposedLabel: string;   // "移除所有未曝光資料"

  // 欄位與標籤
  formatField: (key: string) => string;   // 各平台自己的標籤轉換
  reportList: ReportListItem[];           // 報表類型清單
  metricsGroups: MetricsGroup[];          // 數據類型群組 (用 Meta 的分類作法統一三端)
  extraOptionsRender?: React.FC<{ 
    value: any; 
    onChange: (next: any)=>void;
  }>; // 例如 Google 的 AdTypes 多選；LINE 若不需要可不傳
  getExtraFromTemplate?: (template?: any) => any;

  // 帳戶/活動選擇器（平台各自實作）
  AccountSelect: React.FC<{ onSelectAccount: (acc: any)=>void }>;
  CampaignSelect: React.FC<{ onSelectCampaign: (c: {accountId: string; id: string; name: string})=>void }>;
  MarkupButton?: React.ComponentType; // 你已有 CreateMarkupButton / isMeta

  // GraphQL hooks（以函式方式注入，避免與平台耦合）
  useGetTemplatesQuery: () => { data?: { templates: any[] }; loading: boolean; error?: any };
  useCreateTemplateMutation: () => [(args: any)=>Promise<any>];
  useUpdateTemplateMutation: () => [(args: any)=>Promise<any>];
  useRemoveTemplateMutation: () => [(args: any)=>Promise<any>];
  useCreateRecordMutation: () => [(args: any)=>Promise<any>];
  useUpdateTemplateAssigneeMutation: () => [(args: any)=>Promise<any>]; // 新增這行

  // GraphQL 相關字串（refetch 名稱、Record input 名稱）
  refetchTemplatesQueryName: string; // ex: "getGoogleAdsTemplates"/"getMetaTemplates"/"getLineTemplates"
  refetchRecordListQueryName: string; // ex: "getGoogleAdsRecordList"...
  recordInputKey: string;    // ex: "googleAdsRecordInput"/"metaAdsRecordInput"/"lineAdsRecordInput"
  templateInputKey: string;    // ex: "googleAdsTemplateInput"/"metaAdsTemplateInput"/"lineAdsTemplateInput"

  // DTO 組裝（平台可能有額外欄位，如 google 的 adTypes）
  buildTemplateInput: (state: {
    name: string;
    reportNames: string[];
    metricsFields: string[];
    accountCampaigns: AccountCampaign[];
    assigneeId?: string | number;
    removeUnexposed?: boolean;
    extra?: any; // 例如 adTypes
  }) => any;
}